import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { getValue } from '@ourbranch/lookups';
import PropTypes from 'prop-types';

import { Button } from 'core/components/button';
import { AuthContext } from 'core/components/auth';
import { Label } from 'core/components/label';
import NotificationType from 'core/helpers/notifications';
import { useStore } from 'core/store';
import { NotificationCard } from 'core/components/notification-card';
import useSession from 'core/hooks/use-session';
import useStyles from './notification.styles';

const notificationsToFilterOutAtCheckout = [
  NotificationType.Offer.OfferNeedsUWReview,
  NotificationType.Offer.SelfReportedUWFlags,
  NotificationType.Offer.OfferWithOlderRoof
];

const AbodeProPlan = ({ classes }) => (
  <Label className={classes.styledText}>
    {'This offer was generated using a special link that includes '}
    <strong>{'an additional $100 charge for the Abode Pro plan.'}</strong>
    {` This charge is separate from your premium and must be paid by credit card. If you do not want the Abode Pro Plan included, please turn off the
        Connected Home discount in the Settings tab and update the offer.`}
  </Label>
);
AbodeProPlan.propTypes = {
  classes: PropTypes.object
};

const NewConnectedHomeSignUpCustomer = ({ classes }) => (
  <Label className={classes.styledText}>
    {
      'A new sign up will prompt the home security company to contact the member to buy the home security system necessary to retain the Connected Home Discount. Available with select home security partnerships only.'
    }
  </Label>
);
NewConnectedHomeSignUpCustomer.propTypes = {
  classes: PropTypes.object
};

const ConnectedHome = ({ classes }) => (
  <Label className={classes.styledText}>
    {`This offer has an affinity discount from a Connected Home partner, but does not include a Connected Home discount,
        resulting in an incorrect price. To correct this, go to the Settings tab and `}
    <strong>{`make sure the Connected Home discount is turned on and has devices
        added, or remove the affinity discount.`}</strong>
  </Label>
);
ConnectedHome.propTypes = {
  classes: PropTypes.object
};

const OfferNeedsUWReview = () => (
  <Label>
    Due to the age of the home, Branch needs additional confirmations before home policy eligibility can be confirmed.
    Please continue to the Underwriting Review page to verify characteristics like plumbing, electrical, or siding.
  </Label>
);
OfferNeedsUWReview.propTypes = {
  option: PropTypes.string
};

const OfferWithOlderRoof = () => <Label>Please verify the roof year to ensure an accurate premium calculation.</Label>;

const SelfReportedUWFlags = ({ classes, canClearUWFormRejections, setShowUWResetModal, offerStore }) => {
  let ineligibleForHome;
  let ineligibleForAuto;
  if (
    offerStore?.offer?.quote?.preBindUWCheck?.autoVerification?.ineligibleForAutoDueToUWSelfReport ||
    offerStore?.priorQuoteWithPreBindUWRejections?.ineligibleForAuto
  ) {
    ineligibleForAuto = true;
  }

  if (
    offerStore?.offer?.quote?.preBindUWCheck?.homeVerification?.ineligibleForHomeDueToUWSelfReport ||
    offerStore?.priorQuoteWithPreBindUWRejections?.ineligibleForHome
  ) {
    ineligibleForHome = true;
  }

  return (
    <Grid container className={classes.UWContainer}>
      <Label>
        {`During the sales underwriting review this member reported characteristic(s) that do not meet Branch
        underwriting guidelines, making them ineligible for ${
          ineligibleForHome ? (ineligibleForAuto ? 'home and auto' : 'home') : 'auto'
        } insurance.`}
        {canClearUWFormRejections && (
          <Button
            className={classes.verifyBtn}
            variant="contained"
            color="primary"
            onClick={() => setShowUWResetModal(true)}
          >
            Reset
          </Button>
        )}
      </Label>
    </Grid>
  );
};
SelfReportedUWFlags.propTypes = {
  classes: PropTypes.object,
  canClearUWFormRejections: PropTypes.bool,
  setShowUWResetModal: PropTypes.func,
  offerStore: PropTypes.object
};

const LapsedDriversLicenses = ({ state }) => {
  const stateValue = getValue('usStates', state);

  return (
    <Label>
      This is a {stateValue} Offer. Please ensure the age first licensed and driver's license lapse status is accurate
      for all drivers before continuing.
    </Label>
  );
};
LapsedDriversLicenses.propTypes = {
  state: PropTypes.string
};

const scrollRejectionsIntoView = () => {
  document.getElementById('policyErrors')?.scrollIntoView({ behavior: 'smooth' });
};

const HomeRejection = ({ classes }) => {
  return (
    <>
      <Label className={classes.styledText}>
        <strong>The home policy is ineligible.</strong>
        <br />
        During the check for home incidents, this house has characteristics that make it ineligible for a home policy.
      </Label>
      <Button onClick={scrollRejectionsIntoView} className={classes.boldButton} variant="contained" color="primary">
        Learn more
      </Button>
    </>
  );
};
HomeRejection.propTypes = {
  classes: PropTypes.object
};

const CannotPurchase = ({ classes }) => {
  return (
    <>
      <Label className={classes.styledText}>
        <strong>One or more policies on this offer cannot be purchased</strong>
        <div>
          This offer has characteristics that do not meet Branch's underwriting guidelines. These may or may not be
          solvable. See the offer rejections section for more information on potential next steps.
        </div>
      </Label>

      <Button onClick={scrollRejectionsIntoView} className={classes.boldButton} variant="contained" color="primary">
        View rejections
      </Button>
    </>
  );
};
CannotPurchase.propTypes = {
  classes: PropTypes.object
};

const scrollToNonRatedIndividuals = () => {
  document.getElementById('nonRatedIndividuals')?.scrollIntoView({ behavior: 'smooth' });
};

const PotentialDriversIdentified = ({ classes }) => {
  return (
    <>
      <Label className={classes.styledText}>
        <div className={classes.bannerTitle}>Potential drivers identified</div>
        <div>
          Our trusted data sources have flagged additional individuals in the household who may need to be added as
          drivers. Review their details and either add them as drivers or provide a reason they shouldn’t be included.
        </div>
      </Label>

      <Button onClick={scrollToNonRatedIndividuals} className={classes.boldButton} variant="contained" color="primary">
        Review now
      </Button>
    </>
  );
};
PotentialDriversIdentified.propTypes = {
  classes: PropTypes.object
};

const SkipUnderwritingActive = ({ classes, offerStore }) => {
  const skipUntilDate = offerStore?.offer?.skipUnderwritingUntil;
  let formattedDate = '';

  if (skipUntilDate) {
    try {
      const date = new Date(skipUntilDate);
      formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      formattedDate = skipUntilDate;
    }
  }

  return (
    <Label className={classes.styledText}>
      <strong>Underwriting rules are currently skipped</strong>
      <div>
        This offer is bypassing underwriting rules until {formattedDate}. After this time, normal underwriting
        validation will resume automatically.
      </div>
    </Label>
  );
};
SkipUnderwritingActive.propTypes = {
  classes: PropTypes.object,
  offerStore: PropTypes.object
};

const OfferNotifications = observer(({ type, setShowUWResetModal }) => {
  const { offer: offerStore } = useStore();
  const { canClearUWFormRejections } = useContext(AuthContext);
  const { isAgency } = useSession();
  const classes = useStyles();
  const location = useLocation();

  const atCheckout = location.pathname.includes('checkout');

  let notifications = offerStore.notifications;

  if (atCheckout) {
    notifications = notifications.filter(
      (notificationType) => !notificationsToFilterOutAtCheckout.includes(notificationType)
    );
  }

  // NEED TO FIND WHERE WE ARE ADDING THESE TO REMOVE THEM FOREVER
  notifications = notifications.filter((notificationType) => notificationType !== NotificationType.Offer.HomeRejection);

  return (
    <Grid id="notificationsContainer">
      {notifications.map((notificationType) => (
        <NotificationCard key={notificationType} type={type}>
          {notificationType === NotificationType.Offer.HomeRejection && <HomeRejection classes={classes} />}
          {notificationType === NotificationType.Offer.CannotPurchase && <CannotPurchase classes={classes} />}
          {notificationType === NotificationType.Offer.AbodeProPlan && <AbodeProPlan classes={classes} />}
          {notificationType === NotificationType.Offer.ConnectedHome && <ConnectedHome classes={classes} />}
          {notificationType === NotificationType.Offer.NewConnectedHomeSignUpCustomer && (
            <NewConnectedHomeSignUpCustomer classes={classes} />
          )}
          {notificationType === NotificationType.Offer.OfferNeedsUWReview && <OfferNeedsUWReview />}
          {notificationType === NotificationType.Offer.OfferWithOlderRoof && <OfferWithOlderRoof />}
          {notificationType === NotificationType.Offer.SelfReportedUWFlags && (
            <SelfReportedUWFlags
              classes={classes}
              canClearUWFormRejections={canClearUWFormRejections}
              setShowUWResetModal={setShowUWResetModal}
              offerStore={offerStore}
            />
          )}
          {notificationType === NotificationType.Offer.LapsedDriversLicenses && (
            <LapsedDriversLicenses state={offerStore.state} />
          )}
          {notificationType === NotificationType.Offer.PotentialDriversIdentified && (
            <PotentialDriversIdentified classes={classes} />
          )}
          {notificationType === NotificationType.Offer.SkipUnderwritingActive && !isAgency && (
            <SkipUnderwritingActive classes={classes} offerStore={offerStore} />
          )}
        </NotificationCard>
      ))}
    </Grid>
  );
});

export default OfferNotifications;
